import { useState, useEffect } from 'react'
import { authApi } from '../../api/auth'
import { useUiStore } from '../../stores/uiStore'
import { Spinner } from '../ui/Spinner'

interface Session {
  id: string
  device_name: string
  ip_address: string
  is_current: boolean
  last_seen_at: string
  created_at: string
}

interface Props { onBack: () => void }

export function DevicesPanel({ onBack }: Props) {
  const { addToast } = useUiStore()
  const [sessions, setSessions] = useState<Session[]>([])
  const [loading, setLoading] = useState(true)
  const [revokingId, setRevokingId] = useState<string | null>(null)

  useEffect(() => {
    authApi.listSessions()
      .then((r: unknown) => setSessions(((r as { sessions?: Session[] }).sessions) ?? []))
      .catch(() => addToast('error', 'Не удалось загрузить устройства'))
      .finally(() => setLoading(false))
  }, [addToast])

  const handleRevoke = async (id: string) => {
    setRevokingId(id)
    try {
      await authApi.revokeSession(id)
      setSessions((s) => s.filter((x) => x.id !== id))
      addToast('success', 'Устройство отключено')
    } catch {
      addToast('error', 'Не удалось отключить устройство')
    } finally {
      setRevokingId(null)
    }
  }

  const handleRevokeAll = async () => {
    const others = sessions.filter((s) => !s.is_current)
    for (const s of others) {
      await authApi.revokeSession(s.id).catch(() => {})
    }
    setSessions((s) => s.filter((x) => x.is_current))
    addToast('success', 'Все другие устройства отключены')
  }

  // Текущее устройство первым, остальные по последней активности.
  const sorted = [...sessions].sort((a, b) => {
    if (a.is_current !== b.is_current) return a.is_current ? -1 : 1
    return (b.last_seen_at ?? '').localeCompare(a.last_seen_at ?? '')
  })
  const hasOthers = sessions.some((s) => !s.is_current)

  return (
    <div className="flex flex-col h-full bg-bg-primary">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-divider shrink-0">
        <button onClick={onBack} className="text-txt-secondary hover:text-txt-primary transition-colors">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 5l-7 7 7 7"/>
          </svg>
        </button>
        <h2 className="text-txt-primary font-semibold text-base flex-1">Устройства</h2>
        {hasOthers && (
          <button onClick={handleRevokeAll} className="text-red-400 text-sm hover:text-red-300 transition-colors">
            Отключить все
          </button>
        )}
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center"><Spinner /></div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          <p className="text-txt-secondary text-xs uppercase tracking-wider font-medium mb-1">Подключённые устройства</p>
          {sorted.length === 0 && (
            <p className="text-txt-meta text-sm py-6 text-center">Нет активных устройств</p>
          )}
          {sorted.map((s) => (
            <div key={s.id} className={`flex items-start gap-3 p-2.5 rounded-xl ${s.is_current ? 'bg-accent/10 border border-accent/20' : 'bg-bg-input'}`}>
              <div className="text-2xl shrink-0">
                {/mobile|android|iphone|dart|okhttp/i.test(s.device_name) ? '📱' : '💻'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-txt-primary text-sm font-medium truncate">
                  {s.device_name || 'Неизвестное устройство'}
                  {s.is_current && <span className="ml-2 text-accent text-xs">это устройство</span>}
                </p>
                {s.ip_address && <p className="text-txt-meta text-xs">{s.ip_address}</p>}
                {s.last_seen_at && (
                  <p className="text-txt-meta text-xs">{new Date(s.last_seen_at).toLocaleString()}</p>
                )}
              </div>
              {!s.is_current && (
                <button
                  onClick={() => handleRevoke(s.id)}
                  disabled={revokingId === s.id}
                  className="text-red-400 hover:text-red-300 text-xs shrink-0 disabled:opacity-40"
                >
                  {revokingId === s.id ? '...' : 'Отключить'}
                </button>
              )}
            </div>
          ))}
          <p className="text-txt-meta text-xs pt-2">
            Если вы не узнаёте устройство — отключите его. На нём придётся войти заново.
          </p>
        </div>
      )}
    </div>
  )
}
